<script setup lang="ts">
  import { ref, toRef, watch } from 'vue';
  import { Loader2 } from 'lucide-vue-next';
  import { IconImage, Checkbox, Tooltip, InfoTooltip } from '@/shared/ui';
  import {
    functionalActivityApi,
    type FunctionalActivity,
    type FunctionalActivityMember,
  } from '@/entities/FunctionalActivity';
  import PencilIcon from '@/shared/assets/icons/PencilGray.svg';
  import CopyIcon from '@/shared/assets/icons/CopyIcon.svg';
  import TrashIcon from '@/shared/assets/icons/TrashIcon.svg';
  import InfoIcon from '@/shared/assets/icons/InfoIcon.svg';
  import SmallChevronUpIcon from '@/shared/assets/icons/SmallChevronUp.svg';
  import { formatMemberAuditDateTime } from '@/pages/admin/lib';

  const props = defineProps<{
    activity: FunctionalActivity;
    isExpanded: boolean;
    isSelected: boolean;
  }>();

  const emit = defineEmits<{
    'toggle-selection': [id: number];
    'toggle-expand': [];
    edit: [id: number];
    clone: [id: number];
    delete: [id: number];
  }>();

  const members = ref<FunctionalActivityMember[]>([]);
  const loadingMembers = ref(false);
  const membersLoaded = ref(false);
  const membersError = ref(false);
  const isExpanded = toRef(props, 'isExpanded');

  const loadMembers = () => {
    loadingMembers.value = true;
    membersError.value = false;
    functionalActivityApi
      .getMembers(props.activity.id)
      .then((loadedMembers) => {
        members.value = loadedMembers;
        membersLoaded.value = true;
      })
      .catch((error) => {
        console.error('Erro ao carregar membros da atividade funcional:', error);
        membersError.value = true;
      })
      .finally(() => {
        loadingMembers.value = false;
      });
  };

  watch(
    isExpanded,
    (expanded) => {
      if (expanded && !membersLoaded.value && !loadingMembers.value) {
        loadMembers();
      }
    },
    { immediate: true }
  );

  const toggleSelection = () => {
    emit('toggle-selection', props.activity.id);
  };

  const toggleExpand = () => {
    emit('toggle-expand');
  };

  const handleEdit = () => {
    emit('edit', props.activity.id);
  };

  const handleClone = () => {
    emit('clone', props.activity.id);
  };

  const handleDelete = () => {
    emit('delete', props.activity.id);
  };

  const actions = [
    { icon: PencilIcon, tooltip: 'Editar', handler: handleEdit },
    { icon: CopyIcon, tooltip: 'Clonar', handler: handleClone },
    { icon: TrashIcon, tooltip: 'Deletar', handler: handleDelete },
  ];
</script>

<template>
  <tr class="transition-colors duration-300 ease-out">
    <td class="w-20 align-middle text-center">
      <Checkbox
        :id="activity.id"
        :checked="isSelected"
        @toggle="toggleSelection"
      />
    </td>
    <td class="pl-6 py-4.5 text-sm text-gray-07 text-left">
      {{ activity.name }}
    </td>
    <td class="pl-6 py-4 text-sm text-gray-07 w-50">
      <div class="flex gap-2 text-gray-xx-02 items-center">
        <Tooltip
          v-for="action in actions"
          :key="action.tooltip"
          :content="action.tooltip"
          placement="top"
          class="flex items-center"
        >
          <button @click="action.handler">
            <IconImage
              :src="action.icon"
              :alt="action.tooltip"
            />
          </button>
        </Tooltip>
        <InfoTooltip
          :icon="InfoIcon"
          title="Informações"
          :created-at="formatMemberAuditDateTime(activity.createdAt)"
          :created-by="activity.creator?.name"
          :updated-by="activity.updater?.name"
          :updated-at="formatMemberAuditDateTime(activity.updatedAt)"
          placement="top"
        >
          <button>
            <IconImage
              :src="InfoIcon"
              alt="Informações"
            />
          </button>
        </InfoTooltip>
      </div>
    </td>
    <td class="w-12 text-center">
      <button
        class="p-2 border border-gray-02 rounded-lg shadow-sm"
        data-testid="expand-functional-activity"
        @click="toggleExpand"
      >
        <IconImage
          :src="SmallChevronUpIcon"
          alt="Expandir"
          class="transition-transform duration-200"
          :class="{ 'rotate-180': !isExpanded }"
        />
      </button>
    </td>
  </tr>
  <template v-if="isExpanded">
    <tr v-if="loadingMembers">
      <td
        colspan="4"
        class="bg-gray-02/30 pl-12 py-3 text-sm text-gray-05"
      >
        <div class="flex items-center gap-2">
          <Loader2 class="w-4 h-4 animate-spin text-gray-05" />
          Carregando membros...
        </div>
      </td>
    </tr>
    <tr v-else-if="membersError">
      <td
        colspan="4"
        class="bg-gray-02/30 pl-12 py-3 text-sm text-red-500"
      >
        Erro ao carregar membros. Tente novamente.
      </td>
    </tr>
    <template v-else>
      <tr>
        <td
          colspan="4"
          class="bg-gray-02/30 pl-12 py-3 text-sm font-semibold text-gray-07"
        >
          Membros
        </td>
      </tr>
      <tr v-if="members.length === 0">
        <td
          colspan="4"
          class="bg-gray-02/30 pl-12 py-3 text-sm text-gray-05"
        >
          Nenhum membro vinculado a esta atividade.
        </td>
      </tr>
      <tr
        v-for="member in members"
        :key="member.id"
 class="bg-gray-02/30"
      >
        <td
          colspan="4"
          class="pl-12 py-3 text-sm text-gray-07"
        >
          {{ member.userName }}
        </td>
      </tr>
    </template>
  </template>
</template>
